import React, {useState} from "react";
import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {LoadingButton, ToggleButtonGroup} from "@mui/lab";
import {useVerificationDialog} from "./useVerificationDialog";
import {useLocales} from "../../../locales";
import MethodButton from "../../../components/MethodButton";
import {fCurrency} from "../../../utils/formatNumber";
import SeatsLayout from "./SeatsLayout";
import {getTimeFromData} from "../../../utils/formatTime";

export default function VerificationDialog(props) {
    const {translate} = useLocales();
    const [selectedSeats, setSelectedSeats] = useState([]);
    const {
        loading, totalPassengers, openSelect, selectedPayments, exchange, cardNumbers,
        roundTotal, filteredPayments, availableSeats,
        handleChangePassengers, handleOpenSelect, handleCloseSelect,
        handleMethodClick,
        handleChangeAmount, handleChangeCardNumber,
        handleVerificationCard, getLabel
    } = useVerificationDialog({...props, selectedSeats});

    const {open, onClose, tripData} = props;

    const {operatorName, lineName, departureTripTime} = tripData || {};

    return (
        <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
            <form>
                <DialogContent>
                    <Stack spacing={1.5} mb={2}>
                        <Typography
                            textAlign="center"
                            variant="body2"
                            fontWeight="bold"
                        >
                            {translate("ticketVerification")}
                        </Typography>

                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="stretch"
                            flexWrap="wrap"
                            gap={2}
                        >
                            {[
                                {label: translate("carrier"), value: operatorName},
                                {label: translate("lineName"), value: lineName},
                                {label: translate("departureTime"), value: getTimeFromData(departureTripTime)}
                            ].map((item, index) => (
                                <Card
                                    key={index}
                                    sx={{
                                        flex: '1 1 30%',
                                        minWidth: 200,
                                        borderRadius: 2,
                                        boxShadow: 2,
                                        color: (theme) =>
                                            theme.palette.mode === "light" ? "primary.darker" : "primary.lighter",
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                                        px: 3,
                                        py: 2,
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography variant="body2">
                                        <strong>{item.label}:</strong> {item.value}
                                    </Typography>
                                </Card>
                            ))}
                        </Stack>
                    </Stack>

                    <Stack
                        direction="row"
                        gap={2}
                        alignItems="flex-start"
                        mt={1}
                        mb={2}
                        divider={<Divider orientation="vertical" flexItem/>}
                    >

                        <FormControl sx={{width: 120}} size="small">
                            <InputLabel id="total-passengers">{translate("numberOfPassengers")}</InputLabel>
                            <Select
                                labelId="total-passengers-label"
                                id="total-passengers-controlled-open-select"
                                open={openSelect}
                                onClose={handleCloseSelect}
                                onOpen={handleOpenSelect}
                                value={totalPassengers}
                                label={translate("numberOfPassengers")}
                                onChange={handleChangePassengers}
                            >
                                {Array.from({length: tripData?.freeSeats}, (_, index) => (
                                    <MenuItem key={index + 1} value={index + 1}>
                                        {index + 1}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={1}
                            sx={{
                                flex: 1,
                                minWidth: 220,
                                alignContent: "flex-start"
                            }}
                        >
                            {cardNumbers.map((number, index) => (
                                <TextField
                                    size="small"
                                    key={index}
                                    label={`${translate("cardNumber")} ${index + 1}`}
                                    value={number}
                                    onChange={(e) => handleChangeCardNumber(index, e.target.value)}
                                    sx={{
                                        flex: "1 1 120px",
                                        minWidth: 120
                                    }}
                                />
                            ))}
                        </Stack>
                    </Stack>

                    {tripData?.alSeatNumbered && (
                        <SeatsLayout
                            totalPassengers={totalPassengers}
                            filteredSeats={availableSeats}
                            tripData={tripData}
                            loading={loading}
                            onChangeSelectedSeats={setSelectedSeats}
                        />
                    )}

                    <Divider sx={{borderStyle: "dashed", mb: 1}}>
                        <Chip label={translate("paymentType")}/>
                    </Divider>

                    <Card
                        sx={{
                            color: (theme) => (theme.palette.mode === "light" ? "primary.darker" : "primary.lighter"),
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                            paddingX: 3, paddingY: 2
                        }}>
                        <Stack direction="row" gap={1} justifyContent="space-between">
                            <ToggleButtonGroup
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    flexWrap: "wrap"
                                }}
                            >
                                {filteredPayments.map((method, idx) => (
                                    <MethodButton
                                        key={`payment-method-${method.name}-${idx}`}
                                        onClick={handleMethodClick}
                                        method={method}
                                        selectedPayments={selectedPayments}
                                    />
                                ))}
                            </ToggleButtonGroup>

                            <>
                                <Box>
                                    <Stack direction="column" spacing={1}>
                                        <Typography variant="button" color="dimgrey">
                                            {translate("totalAmountForPay")}: {fCurrency(roundTotal.toString())}
                                        </Typography>

                                        <Stack direction="row" spacing={1}>
                                            {selectedPayments.map((value, index) => (
                                                <TextField
                                                    size="small"
                                                    autoFocus={index === 0}
                                                    key={`selected-payment-${index}-${value.paymentType}`}
                                                    sx={{maxWidth: 150}}
                                                    label={getLabel(value.paymentType)}
                                                    value={value.amount}
                                                    name={value.paymentType}
                                                    onChange={(event) => handleChangeAmount(event, index)}
                                                />
                                            ))}
                                        </Stack>

                                        {exchange > 0 ? (
                                            <Typography variant="button" color="dimgrey">
                                                Kusur: {fCurrency(exchange.toString())}
                                            </Typography>
                                        ) : exchange < 0 ? (
                                            <Typography variant="button" color="darkRed">
                                                Uneli ste manji iznos od predviđenog: {fCurrency(exchange.toString())}
                                            </Typography>
                                        ) : (
                                            <Typography variant="button"/>
                                        )}
                                    </Stack>
                                </Box>
                                <Stack gap={1}>
                                    <Button disabled={loading} variant="outlined" color="inherit" onClick={onClose}>
                                        {`${translate("giveUp")} [ESC]`}
                                    </Button>
                                    <LoadingButton disabled={exchange < 0} loading={loading} onClick={handleVerificationCard}
                                                   variant="contained">
                                        {translate("fiscalize")}
                                    </LoadingButton>
                                </Stack>
                            </>

                        </Stack>
                    </Card>
                </DialogContent>
            </form>
        </Dialog>
    );
}
